import { get, toLower } from 'lodash';
import moment from 'moment';
import {
  GridColumn,
  GridColumnVisibilityModel,
  GridSlots,
} from 'libs/mui/data-grid/types/data-grid-props.type';
import { OrgStaticData, OrganizationalTagPagesTypeEnum } from 'features/static-data';
import { EllipsisText } from 'common/components/ellipsis-text/ellipsis-text.component';
import { Stack } from 'common/components/material';
import CXDataGridManageColumnsButton from 'common/components/cx-data-grid/cx-data-grid-manage-columns-button.component';
import StaticDataLink from '../static-data/components/static-data-link.component';
import { PersonLink } from 'common/components/person-link/person-link.component';
import EmptySearchView from 'features/explore/components/EmptySearchView';
import CXDataGridFooter from 'common/components/cx-data-grid/cx-data-grid-footer.component';
import {
  StyledColumnMenu,
  StyledColumnsPanel,
} from 'common/components/cx-data-grid/cx-data-grid.style';
import LinearProgress from 'common/components/material/LinearProgress';
import { titleCase } from 'common/utils/app.utils';

export const ROW_HEIGHT = 36;
export const HORIZONTAL_SCROLL_OFFSET = 20;
// Total height of data grid header + footer + horizontal scrollbar
export const TOTAL_OFFSET = ROW_HEIGHT * 2 + HORIZONTAL_SCROLL_OFFSET;
export const MAX_GRID_HEIGHT = 750;
// Increased from 10 to reduce the amount of times we fetch as users scroll down the grid
export const PAGE_SIZE = 30;
export const OVERLAY_HEIGHT = 480;

export const SLOTS = {
  noRowsOverlay: EmptySearchView,
  footer: CXDataGridFooter,
  columnMenu: StyledColumnMenu,
  columnsPanel: StyledColumnsPanel,
  loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
};

export const DEFAULT_VISIBILITY_MODEL = {
  firstName: false,
  lastName: false,
  department2: false,
  division2: false,
  product2: false,
} as GridColumnVisibilityModel;

const getHierarchicalStaticDataColumn = (
  staticDataType: OrganizationalTagPagesTypeEnum,
  level: number,
): GridColumn => {
  const key = toLower(staticDataType);
  const label = titleCase(staticDataType);
  const field = `${key}${level}`;
  const headerName = `${label} L${level}`;
  return {
    field,
    headerName,
    width: 150,
    valueGetter: (value, row) =>
      get(row, `organizationProfile.${key}.hierarchy.[${level}].name`),
    renderCell: ({ row }) => {
      const staticData = get(row, `organizationProfile.${key}.hierarchy.[${level}]`);
      return (
        <StaticDataLink
          key={staticData?.id}
          type={staticDataType}
          id={staticData?.id}
          name={staticData?.name}
          isVisible
        />
      );
    },
  };
};

export const ProfileGridColumns: GridColumn[] = [
  {
    field: 'id',
    headerName: 'ID',
    width: 100,
    // value is only defined if the "field" matches a top-level property in the profile object
    valueGetter: (value, row) => {
      return get(row, 'organizationProfile.externalId');
    },
  },
  {
    field: 'fullName',
    headerName: 'Full Name',
    width: 200,
    renderCell: ({ row }) => {
      return (
        <PersonLink
          key={row.profileId}
          id={row.profileId}
          name={row.fullName}
          color="inherit"
          fontWeight={500}
          readonly={row.readonly}
        />
      );
    },
  },
  {
    field: 'firstName',
    headerName: 'First Name',
    width: 150,
  },
  {
    field: 'lastName',
    headerName: 'Last Name',
    width: 150,
  },
  {
    field: 'role',
    headerName: 'Role',
    width: 200,
    valueGetter: (value, profile) => {
      return get(profile, 'organizationProfile.profileHeadline');
    },
  },
  {
    field: 'joinDate',
    headerName: 'Join Date',
    width: 150,
    valueGetter: (value, profile) => {
      return get(profile, 'organizationProfile.joinDate');
    },
    valueFormatter: (value?: string) => {
      if (!value) {
        return '';
      }

      return moment(value, 'YYYY-MM-DD').format('DD MMM yyyy');
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 200,
    valueGetter: (value, profile) => {
      return get(profile, 'organizationProfile.visibleEmail');
    },
  },
  {
    field: 'manager',
    headerName: 'Manager',
    width: 200,
    valueGetter: (value, row) => get(row, 'organizationProfile.manager.fullName'),
    renderCell: ({ row }) => {
      const manager = get(row, 'organizationProfile.manager');
      if (!manager) return;
      return (
        <PersonLink
          key={manager.id}
          id={manager.id}
          name={manager.fullName}
          color="inherit"
          fontWeight={500}
        />
      );
    },
  },
  {
    field: 'country',
    headerName: 'Country',
    width: 150,
    // valueGetter is also used to ensure copying+pasting into Excel works when we have custom rendering
    valueGetter: (value, row) =>
      get(row, 'organizationProfile.workLocation.country.displayName'),
    renderCell: ({ row }) => {
      const country = get(row, 'organizationProfile.workLocation.country');
      return (
        <StaticDataLink
          key={country?.id}
          type={OrganizationalTagPagesTypeEnum.COUNTRY}
          id={country?.id}
          name={country?.displayName}
          isVisible={true}
        />
      );
    },
  },
  {
    field: 'city',
    headerName: 'City',
    width: 150,
    valueGetter: (value, row) =>
      get(row, 'organizationProfile.workLocation.city.displayName'),
    renderCell: ({ row }) => {
      const city = get(row, 'organizationProfile.workLocation.city');
      return (
        <StaticDataLink
          key={city?.id}
          type={OrganizationalTagPagesTypeEnum.CITY}
          id={city?.id}
          name={city?.displayName}
          isVisible={true}
        />
      );
    },
  },
  {
    field: 'office',
    headerName: 'Office',
    width: 150,
    valueGetter: (value, row) => get(row, 'organizationProfile.workLocation.name'),
    renderCell: ({ row }) => {
      const workLocation = get(row, 'organizationProfile.workLocation');
      return (
        <StaticDataLink
          key={workLocation?.id}
          type={OrganizationalTagPagesTypeEnum.WORK_LOCATION}
          id={workLocation?.id}
          name={workLocation?.name}
          isVisible={workLocation?.isVisible}
        />
      );
    },
  },
  getHierarchicalStaticDataColumn(OrganizationalTagPagesTypeEnum.DEPARTMENT, 0),
  getHierarchicalStaticDataColumn(OrganizationalTagPagesTypeEnum.DEPARTMENT, 1),
  getHierarchicalStaticDataColumn(OrganizationalTagPagesTypeEnum.DEPARTMENT, 2),
  getHierarchicalStaticDataColumn(OrganizationalTagPagesTypeEnum.DIVISION, 0),
  getHierarchicalStaticDataColumn(OrganizationalTagPagesTypeEnum.DIVISION, 1),
  getHierarchicalStaticDataColumn(OrganizationalTagPagesTypeEnum.DIVISION, 2),
  getHierarchicalStaticDataColumn(OrganizationalTagPagesTypeEnum.PRODUCT, 0),
  getHierarchicalStaticDataColumn(OrganizationalTagPagesTypeEnum.PRODUCT, 1),
  getHierarchicalStaticDataColumn(OrganizationalTagPagesTypeEnum.PRODUCT, 2),
  {
    field: 'groups',
    headerName: 'Groups',
    width: 200,
    // Disabling group sort for now as it's a nested array and there's some inconsistency when sorting empty values
    sortable: false,
    valueGetter: (value, row) => {
      const groups = get(row, 'organizationProfile.groups', []) as OrgStaticData[];

      if (!groups.length) {
        return;
      }

      // Separate groups with a tab so they are pasted in separate cells in Excel
      return groups.map((group) => group.name).join('\t');
    },
    renderCell: ({ row }) => {
      const groups = get(row, 'organizationProfile.groups', []);

      if (!groups.length) {
        return;
      }

      const firstGroup = get(groups, '[0]');
      const firstGroupLink = (
        <StaticDataLink
          key={firstGroup.id}
          type={OrganizationalTagPagesTypeEnum.GROUP}
          id={firstGroup.id}
          name={
            groups.length > 1 ? `${firstGroup.name} +${groups.length - 1}` : firstGroup.name
          }
          isVisible={firstGroup.isVisible}
        />
      );

      if (groups.length === 1) {
        return firstGroupLink;
      }

      return (
        <Stack justifyContent="center" height="100%" key={firstGroup.id}>
          <EllipsisText
            display="block"
            width="fit-content"
            fontSize="inherit"
            sx={{ textDecoration: 'underline dotted' }}
            lineclamp={1}
            title={
              <Stack>
                {groups.map((group: OrgStaticData) => {
                  return (
                    <StaticDataLink
                      key={group.id}
                      type={OrganizationalTagPagesTypeEnum.GROUP}
                      id={group.id}
                      name={group.name}
                      isVisible={true}
                    >
                      {group.name}
                    </StaticDataLink>
                  );
                })}
              </Stack>
            }
          >
            {firstGroupLink}
          </EllipsisText>
        </Stack>
      );
    },
  },
  {
    field: 'gridActions',
    headerClassName: 'gridActions',
    renderHeader: () => <CXDataGridManageColumnsButton />,
    width: 40,
    minWidth: 40,
    sortable: false,
    resizable: false,
    pinnable: false,
    hideable: false,
    disableColumnMenu: true,
    disableReorder: true,
  },
];

export const ProfileDataGridColNames = ProfileGridColumns.filter(
  (column) => !column.disableReorder,
).map((column) => column.field);
